import { Outlet, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { TID } from "@/lib/testIds";
import { ShoppingBag, Menu, X } from "lucide-react";
import CartDrawer from "@/pages/storefront/CartDrawer";

export default function StoreLayout() {
  const [navPages, setNavPages] = useState([]);
  const [store, setStore] = useState({ store_name: "Blesco" });
  const [open, setOpen] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    http.get("/pages/nav").then((r) => setNavPages(r.data)).catch(() => {});
    http.get("/store/info").then((r) => setStore(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#fafaf8]">
      {/* NAV */}
      <header className="sticky top-0 z-40 bg-[#fafaf8]/85 backdrop-blur-md border-b border-black/5">
        <div className="bls-container flex items-center justify-between py-4">
          <Link to="/" data-testid={TID.navLogo} className="flex items-center gap-1">
            <span className="font-display text-2xl font-bold text-[#16161f]">bles</span>
            <span className="font-display text-2xl font-bold text-[#7f77dd]">co</span>
            <span className="ml-1 mt-1 w-2 h-2 rounded-full bg-[#2a9d6f]" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#16161f]/75">
            <Link to="/products" className="hover:text-[#534ab7] transition-colors">Products</Link>
            {navPages.slice(0, 4).map((p) => (
              <Link key={p.slug} to={`/p/${p.slug}`} className="hover:text-[#534ab7] transition-colors">
                {p.title}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCartOpen(true)}
              data-testid={TID.navCart}
              className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-black/10 hover:border-[#7f77dd] transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag className="w-4 h-4" />
              {count > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#7f77dd] text-white text-[11px] font-bold flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
            <button
              onClick={() => nav("/products")}
              className="hidden sm:inline-flex bls-btn-primary text-sm"
              data-testid="nav-shop-cta"
            >
              Shop now
            </button>
            <button
              className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-black/10"
              onClick={() => setOpen((s) => !s)}
              data-testid={TID.navMenuToggle}
              aria-label="Menu"
            >
              {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {open && (
          <div className="md:hidden border-t border-black/5 bg-white">
            <div className="bls-container py-4 flex flex-col gap-3 text-sm font-medium text-[#16161f]/80">
              <Link to="/products" onClick={() => setOpen(false)}>Products</Link>
              {navPages.map((p) => (
                <Link key={p.slug} to={`/p/${p.slug}`} onClick={() => setOpen(false)}>
                  {p.title}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* CONTENT */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* FOOTER */}
      <footer className="mt-16 border-t border-black/5 bg-white">
        <div className="bls-container py-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-1">
              <span className="font-display text-2xl font-bold text-[#16161f]">bles</span>
              <span className="font-display text-2xl font-bold text-[#7f77dd]">co</span>
            </div>
            <p className="mt-3 text-sm text-[#16161f]/65 leading-relaxed">
              Small upgrades. Big vibes. Curated wellness and lifestyle gadgets.
            </p>
          </div>
          <div>
            <div className="font-display font-bold text-sm uppercase tracking-wider text-[#16161f]/50">Shop</div>
            <ul className="mt-4 space-y-2 text-sm text-[#16161f]/75">
              <li><Link to="/products" className="hover:text-[#534ab7]">All Products</Link></li>
              <li><Link to="/products?featured=true" className="hover:text-[#534ab7]">Bestsellers</Link></li>
              <li><Link to="/cart" className="hover:text-[#534ab7]">Cart</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-display font-bold text-sm uppercase tracking-wider text-[#16161f]/50">Company</div>
            <ul className="mt-4 space-y-2 text-sm text-[#16161f]/75">
              {navPages.map((p) => (
                <li key={p.slug}><Link to={`/p/${p.slug}`} className="hover:text-[#534ab7]">{p.title}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="font-display font-bold text-sm uppercase tracking-wider text-[#16161f]/50">Stay close</div>
            <p className="mt-4 text-sm text-[#16161f]/65">Drops, launches and behind-the-scenes — straight to your inbox.</p>
            <a href="/#notify" className="mt-3 inline-block text-sm font-semibold text-[#534ab7]">Subscribe →</a>
            <div className="mt-6 font-display font-bold text-xs uppercase tracking-wider text-[#16161f]/50">Legal</div>
            <ul className="mt-3 space-y-1.5 text-xs text-[#16161f]/60">
              <li><Link to="/p/privacy" className="hover:text-[#534ab7]">Privacy Policy</Link></li>
              <li><Link to="/p/refund" className="hover:text-[#534ab7]">Refund Policy</Link></li>
              <li><Link to="/p/shipping" className="hover:text-[#534ab7]">Shipping Policy</Link></li>
              <li><Link to="/p/terms" className="hover:text-[#534ab7]">Terms &amp; Conditions</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-black/5">
          <div className="bls-container py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#16161f]/50">
            <span>© {new Date().getFullYear()} {store.store_name || "Blesco"}. All rights reserved.</span>
            <Link to="/admin/login" className="hover:text-[#534ab7]" data-testid="footer-admin-link">Admin</Link>
          </div>
        </div>
      </footer>

      <CartDrawer />
    </div>
  );
}
